'use client';

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import type { Meilisearch_ShippingCountry } from '@/lib/api/meilisearch-types';
import { construireParametresArticlesDevis } from '@/lib/checkout/parametresArticlesDevis';
import type { CartItem } from '@/stores/cartStoreSlices/CartSlice';
import type { ShippingMethodExtended } from '@/stores/cartStoreSlices/ShippingSlice';

import CountryPicker from '../../checkout/components/CountryPicker';
import { ShippingMethodCard } from '../../checkout/components/ShippingMethods';

interface ShippingEstimatorProps {
  cartItems: CartItem[];
  totalPanier: number;
}

// La construction vit désormais dans `@/lib/checkout/parametresArticlesDevis`,
// partagée avec `ShippingSlice`. Elle était recopiée ici, et la copie ne portait
// que le jeton de LIEN PRIVÉ : le même panier affichait 10 € de port sur cette
// page et 0 € à l'étape suivante, parce que l'offre n'était annoncée qu'à l'un
// des deux écrans. Deux écritures de la même règle, c'est un jour où l'une des
// deux ment sur un montant.

export function ShippingEstimator({
  cartItems,
  totalPanier,
}: ShippingEstimatorProps): React.ReactElement | null {
  const t = useTranslations('Panier.ShippingEstimator');
  const [open, setOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] =
    useState<Meilisearch_ShippingCountry | null>(null);
  const [zipCode, setZipCode] = useState('');
  const [results, setResults] = useState<ShippingMethodExtended[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (cartItems.length === 0) return null;

  const countryCode = selectedCountry?.code_iso ?? '';
  const isFrance = countryCode === 'FR';

  // Zip code only needed for France (courier restriction by arrondissement)
  const canEstimate =
    countryCode.length >= 2 && (isFrance ? zipCode.trim().length >= 2 : true);

  const handleEstimate = async (): Promise<void> => {
    if (!canEstimate) return;

    setLoading(true);
    setError(null);
    setResults(null);

    const weightInCart = cartItems.reduce(
      (sum, item) => sum + (item.tranche || 1),
      0,
    );

    const cartItemsParams = construireParametresArticlesDevis(cartItems);

    // For non-France countries, send a dummy zip so the API doesn't reject
    const effectiveZip = isFrance ? zipCode.trim() : '00000';

    try {
      const res = await fetch(
        `/api/checkout/utils/compute-shipping-methods?cartTotal=${totalPanier}&countryCode=${countryCode}&weightInCart=${weightInCart}&zipCode=${effectiveZip}&${cartItemsParams}`,
      );

      if (!res.ok) {
        setError(t('error'));
        return;
      }

      const data = await res.json();
      if (data.error) {
        setError(t('error'));
        return;
      }

      setResults(data);
    } catch {
      setError(t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-sm text-primary-900 hover:underline"
      >
        <span>{t('toggle_label')}</span>
        {open ? (
          <ChevronUpIcon className="size-4" />
        ) : (
          <ChevronDownIcon className="size-4" />
        )}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700">
              {t('country_label')}
            </label>
            <CountryPicker
              selectedCountry={selectedCountry}
              onChange={(country) => {
                setSelectedCountry(country);
                setResults(null);
              }}
              placeholder={t('country_placeholder')}
            />
          </div>

          {isFrance && (
            <div>
              <label
                htmlFor="shipping-zip"
                className="block text-xs font-medium text-gray-700"
              >
                {t('zipcode_label')}
              </label>
              <input
                id="shipping-zip"
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder="75001"
                className="mt-1 w-full rounded-md border-gray-300 text-sm"
              />
            </div>
          )}

          <button
            type="button"
            disabled={!canEstimate || loading}
            onClick={handleEstimate}
            className="w-full rounded-md bg-gray-200 px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? t('loading') : t('submit_label')}
          </button>

          {error && <p className="text-xs text-red-600">{error}</p>}

          {results !== null && results.length === 0 && (
            <p className="text-xs text-gray-500">{t('no_results')}</p>
          )}

          {results !== null && results.length > 0 && (
            <div className="divide-y divide-gray-100">
              {results.map((method) => (
                <div key={method.id} className="py-2">
                  <ShippingMethodCard method={method} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
